import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { 
  Table, 
  Select, 
  DatePicker, 
  Input, 
  Button, 
  Space, 
  Tag, 
  Typography, 
  Pagination, 
  Spin,
  Modal,
  message,
  Tooltip,
  Badge
} from 'antd';
import { 
  CalendarOutlined, 
  SearchOutlined, 
  ReloadOutlined,
  EyeOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import lib from '../../assets/utils/lib';
import { fetchPaginatedData } from '../../assets/utils/lib';
import { notification } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../assets/utils/useAuth';
import AdminAppointmentDetails from './AdminAppointmentDetails';
import useWebSocket from '../../assets/utils/useWebSocket';
import DashboardHero from '../../features/dashboard/components/DashboardHero';
import DashboardSection from '../../features/dashboard/components/DashboardSection';
import { TableSkeleton } from '../../features/dashboard/components/DashboardSkeleton';
import { hasMetricValue, ADMIN_REVIEW_MODAL_CLASS } from '../../features/dashboard/utils';
import './appointments-dashboard.css';

const { RangePicker } = DatePicker;
const { Text } = Typography;
const { Option } = Select;

/**
 * AdminAppointmentsList - Displays all scheduled appointments for Admin/CS users
 */
const AdminAppointmentsList = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [detailsVisible, setDetailsVisible] = useState(false);
  const [convertModalVisible, setConvertModalVisible] = useState(false);
  const [convertingAppointment, setConvertingAppointment] = useState(null);
  const [checkinType, setCheckinType] = useState(null);
  const [checkinTypes, setCheckinTypes] = useState([]);
  const [loadingCheckinTypes, setLoadingCheckinTypes] = useState(false);
  const [confirmModalVisible, setConfirmModalVisible] = useState(false);
  const [confirmingAppointment, setConfirmingAppointment] = useState(null);
  const [confirmationNotes, setConfirmationNotes] = useState('');
  const [confirmSubmitting, setConfirmSubmitting] = useState(false);
  
  // Filters
  const [facilityId, setFacilityId] = useState(null);
  const [status, setStatus] = useState(null);
  const [dateRange, setDateRange] = useState(null);
  const [search, setSearch] = useState('');
  const [facilities, setFacilities] = useState([]);
  const fetchingRef = useRef(false);
  const fetchAppointmentsRef = useRef(null);
  const paginationRef = useRef(pagination);

  // Status options
  const statusOptions = [
    { value: 'scheduled', label: 'Scheduled' },
    { value: 'confirmed', label: 'Confirmed' },
    { value: 'completed', label: 'Completed' },
    { value: 'cancelled', label: 'Cancelled' },
    { value: 'no_show', label: 'No Show' },
    { value: 'rescheduled', label: 'Rescheduled' },
    { value: 'expired', label: 'Expired' },
  ];

  const canConfirmAppointments = useMemo(() => {
    const permissions = user?.profile?.permissions || [];
    return Array.isArray(permissions) && (
      permissions.includes('appointments.manage_all') || permissions.includes('admin.*')
    );
  }, [user]);

  const isConfirmableStatus = (appointmentStatus) =>
    ['scheduled', 'rescheduled'].includes(String(appointmentStatus || '').toLowerCase());

  // Fetch facilities for filter (v2 admin list — do not use legacy searchProviders,
  // which requires a search term and surfaces "Compulsary Fields" on every page load)
  useEffect(() => {
    const fetchFacilities = async () => {
      try {
        const response = await lib.getFacilitiesV2({ page: 1, limit: 1000 });
        const body = response?.data;
        const list = Array.isArray(body?.data)
          ? body.data
          : Array.isArray(body?.data?.facilities)
            ? body.data.facilities
            : [];
        setFacilities(list);
      } catch (error) {
        console.error('Error fetching facilities:', error);
      }
    };
    fetchFacilities();
  }, []);

  // Fetch checkin types when facility filter changes (respects facility + enrollee policy)
  const fetchCheckinTypesForContext = useCallback(async (payload = {}) => {
    setLoadingCheckinTypes(true);
    const $allData = [];
    try {
      const generator = fetchPaginatedData("post", "careTypesList", payload);

      for await (const data of generator) {
        $allData.push(...data);
      }
      setCheckinTypes(
        $allData.map((item) => ({
          label: item?.checkin_type_name?.toUpperCase(),
          value: item?.checkin_type_id,
        }))
      );
      return $allData;
    } catch (error) {
      console.error("Error fetching care types:", error);
      notification.error({
        message: 'Error',
        description: 'Failed to fetch check-in types',
      });
      return [];
    } finally {
      setLoadingCheckinTypes(false);
    }
  }, []);

  useEffect(() => {
    fetchCheckinTypesForContext(facilityId ? { facility_id: facilityId } : {});
  }, [facilityId, fetchCheckinTypesForContext]);

  // Fetch appointments
  const fetchAppointments = useCallback(async (page = 1) => {
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    
    setLoading(true);
    try {
      const params = {
        page,
        pageSize: pagination.pageSize,
      };

      if (facilityId) {
        params.facility_id = facilityId;
      }

      if (status) {
        params.status = status;
      }

      if (dateRange && dateRange.length === 2) {
        params.from = dateRange[0].startOf('day').toISOString();
        params.to = dateRange[1].endOf('day').toISOString();
      }

      if (search && search.trim()) {
        params.search = search.trim();
      }

      const response = await lib.getAdminAppointments(params);
      const body = response?.data || response;
      if (parseInt(body?.error)) {
        // Note: lib.myApi already shows a toast on error; keep this as a fallback
        // for any call sites that bypass lib notifications.
        notification.error({
          message: 'Error',
          description: body?.message || 'Failed to fetch appointments',
        });
        return;
      }

      const responseData = body || {};
      const appointmentsList = Array.isArray(responseData.data) ? responseData.data : [];
      
      setAppointments(appointmentsList);
      setPagination((prev) => ({
        ...prev,
        current: responseData.pagination?.page || page,
        pageSize: responseData.pagination?.pageSize || prev.pageSize,
        total: responseData.pagination?.total || appointmentsList.length || 0,
      }));
    } catch (error) {
      console.error('Error fetching appointments:', error);
      notification.error({
        message: 'Error',
        description: 'Failed to fetch appointments',
      });
    } finally {
      setLoading(false);
      fetchingRef.current = false;
    }
  }, [facilityId, status, dateRange, search, pagination.pageSize]);

  // Keep refs updated
  useEffect(() => {
    fetchAppointmentsRef.current = fetchAppointments;
  }, [fetchAppointments]);

  useEffect(() => {
    paginationRef.current = pagination;
  }, [pagination]);

  useEffect(() => {
    fetchAppointments(pagination.current);
  }, [
    facilityId,
    status,
    dateRange,
    search,
    pagination.current,
    pagination.pageSize,
    fetchAppointments,
  ]);

  // WebSocket connection for real-time updates using reusable hook
  // Messages follow shape: { event, payload, timestamp }
  const { connected: wsConnected } = useWebSocket('appointments', {
    filter: facilityId ? { facility_id: facilityId } : null,
    onMessage: (message) => {
      if (!message || !message.event) {
        return;
      }

      const eventType = message.event;
      const appointmentData = message.payload || {};

      // Filter by facility_id if set (per admin filtering rule)
      if (
        facilityId &&
        appointmentData.facility_id &&
        Number(appointmentData.facility_id) !== Number(facilityId)
      ) {
        return; // Ignore events for other facilities
      }

      // Handle different event types
      switch (eventType) {
        case 'appointment.booked':
        case 'appointment.updated':
        case 'appointment.cancelled':
        case 'appointment.converted':
          // Refresh the appointments list using ref to get latest function
          if (fetchAppointmentsRef.current && paginationRef.current) {
            fetchAppointmentsRef.current(paginationRef.current.current);
          }

          // Show toast notification
          const eventMessages = {
            'appointment.booked': 'New appointment booked',
            'appointment.updated': 'Appointment updated',
            'appointment.cancelled': 'Appointment cancelled',
            'appointment.converted': 'Appointment converted to check-in',
          };

          notification.info({
            message: eventMessages[eventType] || 'Appointment event',
            description: appointmentData.patient_name
              ? `Patient: ${appointmentData.patient_name}`
              : 'Appointment list updated',
            placement: 'topRight',
            duration: 3,
          });
          break;
        default:
          // Unknown / other events are safely ignored
          // console.log('Unknown WebSocket event:', eventType, message);
          break;
      }
    },
  });

  const handlePageChange = (page, pageSize) => {
    setPagination((prev) => ({
      ...prev,
      current: page,
      pageSize: pageSize || prev.pageSize,
    }));
  };

  const handleViewDetails = async (appointment) => {
    setSelectedAppointment(null);
    setDetailsVisible(true);
    
    try {
      const response = await lib.getAdminAppointmentDetails(appointment.appointment_id);
      if (response?.data?.error === 0) {
        setSelectedAppointment(response.data.data);
      } else {
        notification.error({
          message: 'Error',
          description: response?.data?.message || 'Failed to fetch appointment details',
        });
      }
    } catch (error) {
      console.error('Error fetching appointment details:', error);
      notification.error({
        message: 'Error',
        description: 'Failed to fetch appointment details',
      });
    }
  };

  const handleConvertToCheckin = async (appointment) => {
    setConvertingAppointment(appointment);
    setConvertModalVisible(true);
    setCheckinType(null);

    const payload = {
      facility_id: appointment?.facility_id,
      insurance_no: appointment?.insurance_no,
      user_id: appointment?.user_id,
    };
    const types = await fetchCheckinTypesForContext(
      Object.fromEntries(Object.entries(payload).filter(([, v]) => v != null && v !== ''))
    );
    if (types?.length > 0) {
      setCheckinType(types[0].checkin_type_id);
    }
  };

  const handleOpenConfirmModal = (appointment) => {
    setConfirmingAppointment(appointment || null);
    setConfirmationNotes('');
    setConfirmModalVisible(true);
  };

  const handleConfirmAppointment = async () => {
    if (!confirmingAppointment?.appointment_id) return;
    setConfirmSubmitting(true);
    try {
      const payload = {};
      const note = confirmationNotes?.trim();
      if (note) payload.confirmation_notes = note;

      const response = await lib.confirmAdminAppointment(confirmingAppointment.appointment_id, payload);
      const body = response?.data || response;

      if (Number(body?.error) === 0) {
        notification.success({
          message: body?.message || 'Appointment confirmed successfully',
        });
        const updatedStatus = body?.data?.status || 'confirmed';
        setSelectedAppointment(prev => prev?.appointment_id === confirmingAppointment.appointment_id
          ? { ...prev, ...body?.data, status: updatedStatus }
          : prev);
        setConfirmModalVisible(false);
        setConfirmingAppointment(null);
        fetchAppointments(pagination.current);
        return;
      }

      notification.error({
        message: 'Unable to Confirm Appointment',
        description: body?.message || 'Could not confirm appointment.',
      });
    } catch (error) {
      const statusCode = error?.response?.status;
      const backendMessage = error?.response?.data?.message;
      let description = backendMessage || 'Failed to confirm appointment';

      if (statusCode === 403 && !backendMessage) description = 'You do not have permission to confirm appointments.';
      if (statusCode === 404 && !backendMessage) description = 'Appointment not found.';

      notification.error({
        message: 'Unable to Confirm Appointment',
        description,
      });
    } finally {
      setConfirmSubmitting(false);
    }
  };

  // Get checkin type name from ID
  const getCheckinTypeName = (typeId) => {
    if (!typeId) return null;
    const type = checkinTypes.find(t => Number(t.value) === Number(typeId));
    return type ? type.label : null;
  };

  const handleConvertConfirm = async () => {
    if (!convertingAppointment) return;
    
    if (!checkinType) {
      notification.warning({
        message: 'Check-in Type Required',
        description: 'Please select a check-in type',
      });
      return;
    }

    try {
      const response = await lib.convertAppointmentToCheckin(
        convertingAppointment.appointment_id,
        checkinType
      );

      if (response?.data?.error === 0) {
        const checkinCode = response.data.data?.checkin_code;
        message.success(
          response.data.message || 'Appointment converted to check-in successfully'
        );
        setConvertModalVisible(false);
        setConvertingAppointment(null);
        
        // Refresh appointments list
        fetchAppointments(pagination.current);
        
        // Navigate to encounter if checkin_code is available
        if (checkinCode) {
          navigate(`/checkins?code=${encodeURIComponent(checkinCode)}`);
        }
      } else {
        notification.error({
          message: 'Error',
          description: response?.data?.message || 'Failed to convert appointment',
        });
      }
    } catch (error) {
      console.error('Error converting appointment:', error);
      notification.error({
        message: 'Error',
        description: 'Failed to convert appointment',
      });
    }
  };

  const handleResetFilters = () => {
    setFacilityId(null);
    setStatus(null);
    setDateRange(null);
    setSearch('');
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const refreshAppointments = () => {
    fetchAppointments(pagination.current);
  };

  const pageCounts = useMemo(() => {
    let scheduled = 0;
    let confirmed = 0;
    let awaitingCheckin = 0;
    appointments.forEach((item) => {
      const s = String(item?.status || '').toLowerCase();
      if (['scheduled', 'rescheduled'].includes(s)) scheduled += 1;
      if (s === 'confirmed') confirmed += 1;
      if (
        !item?.converted_checkin_code &&
        ['scheduled', 'confirmed', 'rescheduled'].includes(s)
      ) {
        awaitingCheckin += 1;
      }
    });
    return { scheduled, confirmed, awaitingCheckin };
  }, [appointments]);

  const heroMetrics = useMemo(() => {
    const activeStatus = status || null;
    return [
      {
        key: 'total',
        title: 'Total results',
        value: pagination.total,
        color: '#722ed1',
        icon: <UnorderedListOutlined />,
        subtitle: search ? `Search: "${search}"` : 'Matching current filters',
      },
      {
        key: 'scheduled',
        title: 'Scheduled',
        value: pageCounts.scheduled,
        color: '#3497D3',
        icon: <CalendarOutlined />,
        subtitle: activeStatus === 'scheduled' ? 'Filter active' : 'On this page',
        onClick: () => {
          setStatus('scheduled');
          setPagination((prev) => ({ ...prev, current: 1 }));
        },
      },
      {
        key: 'confirmed',
        title: 'Confirmed',
        value: pageCounts.confirmed,
        color: '#49B870',
        icon: <CheckCircleOutlined />,
        subtitle: activeStatus === 'confirmed' ? 'Filter active' : 'On this page',
        onClick: () => {
          setStatus('confirmed');
          setPagination((prev) => ({ ...prev, current: 1 }));
        },
      },
      {
        key: 'awaiting',
        title: 'Awaiting check-in',
        value: pageCounts.awaitingCheckin,
        color: '#faad14',
        icon: <ClockCircleOutlined />,
        subtitle: 'Not yet converted',
      },
    ].filter((m) => hasMetricValue(m.value) || m.key === 'total');
  }, [pagination.total, pageCounts, search, status]);

  const getStatusTag = (status) => {
    const statusConfig = {
      scheduled: { color: 'blue', text: 'Scheduled' },
      confirmed: { color: 'cyan', text: 'Confirmed' },
      completed: { color: 'green', text: 'Completed' },
      cancelled: { color: 'red', text: 'Cancelled' },
      no_show: { color: 'orange', text: 'No Show' },
      rescheduled: { color: 'purple', text: 'Rescheduled' },
      expired: { color: 'volcano', text: 'Expired' },
    };
    const config = statusConfig[status] || { color: 'default', text: status };
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  const columns = [
    {
      title: 'Appointment Time',
      dataIndex: 'scheduled_start',
      key: 'scheduled_start',
      sorter: false,
      render: (text) => {
        if (!text) return 'N/A';
        return dayjs(text).format('MMM D, YYYY h:mm A');
      },
    },
    {
      title: 'Patient',
      key: 'patient',
      render: (record) => (
        <div>
          <Text strong>{record.patient_name || 'N/A'}</Text>
          <br />
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {record.insurance_no || 'N/A'}
          </Text>
        </div>
      ),
    },
    {
      title: 'Facility',
      key: 'facility',
      render: (record) => (
        <div>
          <Text>{record.facility_name || 'N/A'}</Text>
          {(record.facility_city || record.facility_state) && (
            <br />
          )}
          {(record.facility_city || record.facility_state) && (
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {[record.facility_city, record.facility_state].filter(Boolean).join(', ')}
            </Text>
          )}
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => getStatusTag(status),
    },
    {
      title: 'Converted',
      key: 'converted',
      render: (record) => {
        if (record.converted_checkin_code) {
          return (
            <Tooltip title={`Converted at ${record.converted_at ? dayjs(record.converted_at).format('MMM D, YYYY h:mm A') : 'N/A'}`}>
              <Tag color="green" icon={<CheckCircleOutlined />}>
                {record.converted_checkin_code}
              </Tag>
            </Tooltip>
          );
        }
        return <Text type="secondary">Not converted</Text>;
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => handleViewDetails(record)}
          >
            View
          </Button>
          {!record.converted_checkin_code && (
            <Button
              type="link"
              onClick={() => handleConvertToCheckin(record)}
            >
              Convert
            </Button>
          )}
          {canConfirmAppointments && isConfirmableStatus(record?.status) && (
            <Button
              type="link"
              onClick={() => handleOpenConfirmModal(record)}
            >
              Confirm
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div className="appointments-dashboard">
      <div className="dashboard-page-stack">
        <header className="dash-masthead">
          <div className="dash-masthead__lead">
            <h1 className="dash-masthead__title">Appointments</h1>
            <p className="dash-masthead__meta">
              <span>View, confirm, and convert scheduled appointments</span>
            </p>
          </div>
          <div className="dash-masthead__actions">
            <span className={`appointments-live-badge ${wsConnected ? 'is-online' : ''}`}>
              <Badge status={wsConnected ? 'success' : 'default'} />
              {wsConnected ? 'Live' : 'Offline'}
            </span>
            <Button
              type="primary"
              size="small"
              icon={<ReloadOutlined />}
              onClick={refreshAppointments}
              loading={loading}
              className="dash-masthead__refresh"
            >
              Refresh
            </Button>
          </div>
        </header>

        <section className="appointments-overview dash-overview dash-overview--compact" aria-label="Appointments overview">
          <div className="appointments-overview__toolbar">
            <div className="appointments-filter-field">
              <span className="appointments-filter-label">Facility</span>
              <Select
                placeholder="All facilities"
                allowClear
                showSearch
                style={{ width: 180 }}
                value={facilityId}
                onChange={setFacilityId}
                filterOption={(input, option) =>
                  (option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                }
              >
                {facilities.map((facility) => (
                  <Option key={facility.facility_id || facility.id} value={facility.facility_id || facility.id}>
                    {facility.facility_name || facility.name}
                  </Option>
                ))}
              </Select>
            </div>

            <div className="appointments-filter-field">
              <span className="appointments-filter-label">Status</span>
              <Select
                placeholder="All statuses"
                allowClear
                style={{ width: 140 }}
                value={status}
                onChange={setStatus}
              >
                {statusOptions.map((opt) => (
                  <Option key={opt.value} value={opt.value}>
                    {opt.label}
                  </Option>
                ))}
              </Select>
            </div>

            <div className="appointments-filter-field">
              <span className="appointments-filter-label">Date range</span>
              <RangePicker
                placeholder={['From', 'To']}
                value={dateRange}
                onChange={setDateRange}
                format="YYYY-MM-DD"
                style={{ width: 240 }}
              />
            </div>

            <div className="appointments-filter-field appointments-filter-field--grow">
              <span className="appointments-filter-label">Search</span>
              <Input
                placeholder="Insurance, patient, facility, appointment ID…"
                prefix={<SearchOutlined />}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                allowClear
              />
            </div>

            <Button onClick={handleResetFilters}>Reset</Button>
          </div>

          <DashboardHero metrics={heroMetrics} loading={loading && !appointments.length && !pagination.total} />
        </section>

        <DashboardSection
          id="appointments-queue"
          icon={<CalendarOutlined />}
          title="Appointment queue"
          subtitle="Click a row to view details"
          collapsible={false}
          variant="content"
        >
          <div className="appointments-queue-toolbar">
            <span className="appointments-queue-toolbar__count">
              {loading
                ? 'Loading…'
                : `Showing ${appointments.length} of ${pagination.total} appointments`}
            </span>
          </div>

          {loading && !appointments.length ? (
            <TableSkeleton rows={8} columns={6} />
          ) : appointments.length === 0 ? (
            <div className="appointments-empty">
              <CalendarOutlined style={{ fontSize: 32, color: '#94a3b8' }} />
              <p className="appointments-empty__title">No appointments found</p>
              <p className="appointments-empty__desc">
                {search || status || facilityId || dateRange
                  ? 'Try adjusting your filters or search query.'
                  : 'Scheduled appointments will appear here.'}
              </p>
            </div>
          ) : (
            <>
              <Table
                className="appointments-table"
                columns={columns}
                dataSource={appointments}
                rowKey="appointment_id"
                pagination={false}
                size="small"
                scroll={{ x: 'max-content' }}
                onRow={(record) => ({
                  onClick: () => handleViewDetails(record),
                })}
              />

              {pagination.total > 0 ? (
                <div className="appointments-pagination">
                  <Pagination
                    current={pagination.current}
                    pageSize={pagination.pageSize}
                    total={pagination.total}
                    showSizeChanger
                    showTotal={(total, range) =>
                      `${range[0]}-${range[1]} of ${total} appointments`
                    }
                    onChange={handlePageChange}
                    onShowSizeChange={handlePageChange}
                  />
                </div>
              ) : null}
            </>
          )}
        </DashboardSection>
      </div>

      <AdminAppointmentDetails
        visible={detailsVisible}
        appointment={selectedAppointment}
        onClose={() => {
          setDetailsVisible(false);
          setSelectedAppointment(null);
        }}
        onConvert={handleConvertToCheckin}
        onConfirm={handleOpenConfirmModal}
        canConfirmAppointment={canConfirmAppointments}
      />

      {/* Convert to Check-in Modal */}
      <Modal
        rootClassName={ADMIN_REVIEW_MODAL_CLASS}
        className={`${ADMIN_REVIEW_MODAL_CLASS}__dialog`}
        title="Convert appointment to check-in"
        open={convertModalVisible}
        onOk={handleConvertConfirm}
        onCancel={() => {
          setConvertModalVisible(false);
          setConvertingAppointment(null);
        }}
        okText="Convert"
        cancelText="Cancel"
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <Text>
            Select the check-in type for this appointment:
          </Text>
          <Select
            style={{ width: '100%' }}
            value={checkinType}
            onChange={setCheckinType}
            placeholder="Select check-in type"
            loading={loadingCheckinTypes}
            disabled={loadingCheckinTypes || checkinTypes.length === 0}
          >
            {checkinTypes.map((type) => (
              <Option key={type.value} value={type.value}>
                {type.label}
              </Option>
            ))}
          </Select>
          {loadingCheckinTypes && (
            <Text type="secondary" style={{ fontSize: '12px' }}>
              Loading check-in types...
            </Text>
          )}
          {!loadingCheckinTypes && checkinTypes.length === 0 && (
            <Text type="danger" style={{ fontSize: '12px' }}>
              No check-in types available. Please try again later.
            </Text>
          )}
          {convertingAppointment && (
            <div style={{ marginTop: '16px', padding: '12px', background: '#f5f5f5', borderRadius: '4px' }}>
              <Text strong>Appointment Details:</Text>
              <br />
              <Text>Patient: {convertingAppointment.patient_name}</Text>
              <br />
              <Text>Facility: {convertingAppointment.facility_name}</Text>
              <br />
              <Text>
                Scheduled: {dayjs(convertingAppointment.scheduled_start).format('MMM D, YYYY h:mm A')}
              </Text>
            </div>
          )}
        </Space>
      </Modal>

      {/* Confirm Appointment Modal */}
      <Modal
        rootClassName={ADMIN_REVIEW_MODAL_CLASS}
        className={`${ADMIN_REVIEW_MODAL_CLASS}__dialog`}
        title="Confirm appointment"
        open={confirmModalVisible}
        onOk={handleConfirmAppointment}
        onCancel={() => {
          setConfirmModalVisible(false);
          setConfirmingAppointment(null);
          setConfirmationNotes('');
        }}
        okText="Confirm Appointment"
        confirmLoading={confirmSubmitting}
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <Text>
            {confirmingAppointment?.patient_name
              ? `Confirm appointment for ${confirmingAppointment.patient_name}?`
              : 'Confirm this appointment?'}
          </Text>
          <Input.TextArea
            rows={3}
            value={confirmationNotes}
            onChange={(e) => setConfirmationNotes(e.target.value)}
            placeholder="Confirmation notes (optional)"
            maxLength={500}
          />
          {confirmingAppointment?.scheduled_start && (
            <Text type="secondary" style={{ fontSize: '12px' }}>
              Scheduled: {dayjs(confirmingAppointment.scheduled_start).format('MMM D, YYYY h:mm A')}
            </Text>
          )}
        </Space>
      </Modal>
    </div>
  );
};

export default AdminAppointmentsList;
